import { Button, CloseButton, IconButton } from '@ui/shared/Button';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import styled from 'styled-components';
import { LIST_ROW_HEIGHTS } from '../../../constants/list_row_heights';
import {
  closeInnerPopup,
  closePopup,
  openInnerPopup,
  openPopup,
} from '../../../data/providers/popupsProvider';
import { IGroup, IListRowRenderProps, ITeam, Level } from '../../../data/types';
import { CardBody } from '../card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../card/CardHeader';
import { SelectBox } from '../form/SelectBox';
import { List } from '../List/List';
import { ListItem, ListItemCell } from '../List/ListItem';
import { NoResults } from '../NoResults';
import {
  InnerPopupCard,
  PopupCard,
  PopupCardContent,
} from '../popups/PopupCard';

// =============================================================================
// Types
// =============================================================================

type FilterTab = 'teams' | 'groups';

interface IProps {
  teams: ITeam[];
  groups?: IGroup[];
  teamsLoading: boolean;
  groupsLoading?: boolean;
  filteredTeams: string[];
  filteredGroups?: string[];
  applyTeamFilter: (f: string[]) => void;
  applyGroupFilter?: (f: string[]) => void;
  applyFilters?: (teams: string[], groups: string[]) => void;
  level: Level;
  /** Which tabs to show. Defaults to ['teams', 'groups'] */
  showTabs?: FilterTab[];
}

// =============================================================================
// Main Component
// =============================================================================

export const EntityFilter = withRouter(
  (props: IProps & RouteComponentProps): React.ReactElement => {
    const tabs = props.showTabs ?? ['teams', 'groups'];
    const hasActiveFilters =
      props.filteredTeams?.length > 0 ||
      (props.filteredGroups?.length ?? 0) > 0;

    const tooltipText =
      tabs.length === 1
        ? `Filter By ${tabs[0] === 'teams' ? 'Team' : 'Group'}`
        : 'Filter By Team or Group';

    const open = (): void =>
      props.level === 0
        ? openPopup(
            <EntityFilterPopup
              {...props}
              showTabs={tabs}
              onClose={closePopup(props.history)}
            />,
            props.history,
            'entity-filter'
          )
        : openInnerPopup(
            <EntityFilterPopup
              {...props}
              showTabs={tabs}
              onClose={closeInnerPopup(props.history)}
            />,
            props.history,
            'entity-filter'
          );

    return (
      <IconButton
        title={tooltipText}
        icon='filter_list'
        onClick={open}
        color={hasActiveFilters ? 'var(--color-warning-accent)' : undefined}
        style={{ width: 'fit-content' }}
      />
    );
  }
);

// =============================================================================
// Popup Component
// =============================================================================

interface IPopupProps extends IProps {
  onClose: () => void;
  showTabs: FilterTab[];
}

const EntityFilterPopup = (props: IPopupProps): React.ReactElement => {
  const tabs = props.showTabs;
  const showTeams = tabs.includes('teams');
  const showGroups = tabs.includes('groups');
  const showBothTabs = showTeams && showGroups;

  const [activeTab, setActiveTab] = React.useState<FilterTab>(tabs[0]);
  const [filteredTeams, setFilteredTeams] = React.useState<string[]>([]);
  const [filteredGroups, setFilteredGroups] = React.useState<string[]>([]);

  React.useEffect((): void => {
    setFilteredTeams(props.filteredTeams ?? []);
    setFilteredGroups(props.filteredGroups ?? []);
  }, []);

  /**
   * @description Groups filtered to only show those that belong to selected teams
   * If no teams are selected, show all groups
   */
  const groupsAvailableToSelect = React.useMemo((): IGroup[] => {
    if (filteredTeams.length === 0) return props.groups ?? [];
    return (props.groups ?? []).filter((g: IGroup): boolean =>
      g.teams?.some((teamId: string): boolean => filteredTeams.includes(teamId))
    );
  }, [props.groups, filteredTeams]);

  /**
   * @description When selected teams change, remove any selected groups that no longer belong to those teams
   */
  React.useEffect((): void => {
    if (filteredTeams.length === 0) return;
    const validGroupIds = groupsAvailableToSelect.map(
      (g: IGroup): string => g.id
    );
    const stillValidGroups = filteredGroups.filter((id: string): boolean =>
      validGroupIds.includes(id)
    );
    if (stillValidGroups.length !== filteredGroups.length) {
      setFilteredGroups(stillValidGroups);
    }
  }, [filteredTeams.length]);

  const clearAll = (): void => {
    if (showTeams) setFilteredTeams([]);
    if (showGroups) setFilteredGroups([]);
  };

  const teamSelectionHandler = (id: string, checked: boolean): void => {
    let mFiltered = [...(filteredTeams ?? [])];
    if (checked) {
      mFiltered.push(id);
    } else {
      mFiltered = mFiltered.filter((s: string): boolean => s !== id);
    }
    setFilteredTeams(mFiltered);
  };

  const groupSelectionHandler = (id: string, checked: boolean): void => {
    let mFiltered = [...(filteredGroups ?? [])];
    if (checked) {
      mFiltered.push(id);
    } else {
      mFiltered = mFiltered.filter((s: string): boolean => s !== id);
    }
    setFilteredGroups(mFiltered);
  };

  const handleApply = (): void => {
    if (props.applyFilters) {
      props.applyFilters(filteredTeams, filteredGroups);
    } else {
      if (showTeams) props.applyTeamFilter(filteredTeams);
      if (showGroups && props.applyGroupFilter) {
        props.applyGroupFilter(filteredGroups);
      }
    }
    props.onClose();
  };

  const Container = props.level === 1 ? InnerPopupCard : PopupCard;

  const teamCount = filteredTeams?.length || 0;
  const groupCount = filteredGroups?.length || 0;

  const title = showBothTabs
    ? 'Filter Athletes'
    : showTeams
    ? 'Filter By Team'
    : 'Filter By Group';

  return (
    <Container onClose={props.onClose} size='medium' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h5>{title}</h5>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        {showBothTabs && (
          <TabContainer>
            <Tab
              active={activeTab === 'teams'}
              onClick={(): void => setActiveTab('teams')}
            >
              Teams {teamCount > 0 && <TabBadge>{teamCount}</TabBadge>}
            </Tab>
            <Tab
              active={activeTab === 'groups'}
              onClick={(): void => setActiveTab('groups')}
            >
              Groups {groupCount > 0 && <TabBadge>{groupCount}</TabBadge>}
            </Tab>
          </TabContainer>
        )}
        <CardBody>
          {activeTab === 'teams' && showTeams ? (
            <TeamFilterList
              loading={props.teamsLoading}
              teams={props.teams}
              filteredTeams={filteredTeams}
              onSelect={teamSelectionHandler}
            />
          ) : showGroups ? (
            <GroupFilterList
              loading={props.groupsLoading || props.teamsLoading}
              groups={groupsAvailableToSelect}
              filteredGroups={filteredGroups}
              onSelect={groupSelectionHandler}
            />
          ) : null}
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft></CardFooterLeft>
          <CardFooterRight>
            <Button
              onClick={clearAll}
              title='Clear All'
              icon='clear'
              style={{ whiteSpace: 'nowrap' }}
            />
            <Button
              onClick={handleApply}
              title='Apply Filter'
              icon='check'
              style={{ whiteSpace: 'nowrap' }}
            />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </Container>
  );
};

// =============================================================================
// Team List
// =============================================================================

interface ITeamFilterListProps {
  loading: boolean;
  teams: ITeam[];
  filteredTeams: string[];
  onSelect: (id: string, checked: boolean) => void;
}

const TeamFilterList = (props: ITeamFilterListProps): React.ReactElement => {
  const rowRenderer = ({
    index,
    key,
    style,
  }: IListRowRenderProps): React.ReactElement => {
    const row = props.teams[index];

    return (
      <TeamListItem
        style={style}
        team={row as ITeam}
        key={key}
        onSelect={props.onSelect}
        selected={props.filteredTeams?.includes(row.id)}
      />
    );
  };

  return props.teams.length ? (
    <List
      rowHeight={LIST_ROW_HEIGHTS.normal}
      rowRenderer={rowRenderer}
      listLength={props.teams.length}
      renderOnChange={props.filteredTeams}
    />
  ) : (
    <NoResults message='No Teams Found' />
  );
};

interface ITeamListItemProps {
  team: ITeam;
  onSelect: (id: string, checked: boolean) => void;
  style: React.CSSProperties;
  selected: boolean;
}

const TeamListItem = (props: ITeamListItemProps): React.ReactElement => {
  const selectionHandler = (
    e: React.SyntheticEvent<HTMLInputElement>
  ): void => {
    const { checked } = e.currentTarget;
    props.onSelect(props.team.id, checked);
  };
  return (
    <ListItem style={props.style} selected={props.selected}>
      <ListItemCell>{props.team.name}</ListItemCell>
      <ListItemCell>
        <SelectBox selected={props.selected} onSelect={selectionHandler} />
      </ListItemCell>
    </ListItem>
  );
};

// =============================================================================
// Group List
// =============================================================================

interface IGroupFilterListProps {
  loading: boolean;
  groups: IGroup[];
  filteredGroups: string[];
  onSelect: (id: string, checked: boolean) => void;
}

const GroupFilterList = (props: IGroupFilterListProps): React.ReactElement => {
  const rowRenderer = ({
    index,
    key,
    style,
  }: IListRowRenderProps): React.ReactElement => {
    const row = props.groups[index];

    return (
      <GroupListItem
        style={style}
        group={row}
        key={key}
        onSelect={props.onSelect}
        selected={props.filteredGroups?.includes(row.id)}
      />
    );
  };

  return props.groups.length ? (
    <List
      rowHeight={LIST_ROW_HEIGHTS.normal}
      rowRenderer={rowRenderer}
      listLength={props.groups.length}
      renderOnChange={props.filteredGroups}
    />
  ) : (
    <NoResults message='No Groups Found' />
  );
};

interface IGroupListItemProps {
  group: IGroup;
  onSelect: (id: string, checked: boolean) => void;
  style: React.CSSProperties;
  selected: boolean;
}

const GroupListItem = (props: IGroupListItemProps): React.ReactElement => {
  const selectionHandler = (
    e: React.SyntheticEvent<HTMLInputElement>
  ): void => {
    const { checked } = e.currentTarget;
    props.onSelect(props.group.id, checked);
  };
  return (
    <ListItem style={props.style} selected={props.selected}>
      <ListItemCell>{props.group.name}</ListItemCell>
      <ListItemCell>
        <SelectBox selected={props.selected} onSelect={selectionHandler} />
      </ListItemCell>
    </ListItem>
  );
};

// =============================================================================
// Styled Components
// =============================================================================

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-surface-8);
`;

const Tab = styled.button<{ active: boolean }>`
  flex: 1;
  padding: 12px 16px;
  border: none;
  background: transparent;
  color: ${(p): string =>
    p.active ? 'var(--color-primary)' : 'var(--color-secondary-text)'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: ${(p): string =>
      p.active ? 'var(--color-primary)' : 'transparent'};
  }

  &:hover {
    color: var(--color-primary-text);
  }
`;

const TabBadge = styled.span`
  background: var(--color-primary);
  color: white;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
`;
